import type { RawQuestionSettings } from '@/features/content/useComponent';

// What a stored attempts number means. The one reader for the student view,
// the settings dialogs and the control panel's rows, so they cannot disagree
// (issue #54).
//
//   nothing saved, or 0     1 attempt, the default
//   1 to 99                 that many; a multiple-choice question is still
//                           capped at one fewer than its options (by the
//                           student view, which knows the question)
//   -1, or 100 and above    unlimited, never capped. -1 is what Kudu saves;
//                           100 is what the retired app saved for Unlimited.
//
// The new app used to read nothing saved, or 0, as unlimited and 100 as a
// limit. A one-time conversion saved -1 wherever that gave students unlimited
// attempts, so no existing chapter changed when the meaning did.
//
// A chapter's value wins over its course's, and the course's over the
// default. A 0 decides nothing at either level: it is what an unset proto
// field reads as.

export type AttemptField = 'exactAnswer' | 'multipleChoice';
export type AttemptLimit = number | 'unlimited';
export type AttemptSource = 'chapter' | 'course' | 'default';

export interface ResolvedAttempts {
  limit: AttemptLimit;
  source: AttemptSource;
}

export interface SectionAttempts {
  exactAnswer: ResolvedAttempts;
  multipleChoice: ResolvedAttempts;
}

export const DEFAULT_ATTEMPTS = 1;
export const UNLIMITED_ATTEMPTS = -1;
// The retired app's Unlimited. Nothing writes it any more.
const RETIRED_UNLIMITED_ATTEMPTS = 100;
const LISTED_LIMITS = Array.from({ length: 10 }, (_unused, index) => index + 1);

const FIELD_KEYS: Record<AttemptField, readonly [string, string]> = {
  exactAnswer: ['num_attempts_exact_answer', 'numAttemptsExactAnswer'],
  multipleChoice: ['num_attempts_multiple_choice', 'numAttemptsMultipleChoice'],
};

// The stored number, or undefined when the level decides nothing: unset, 0,
// or not a number.
function readStoredAttempts(
  settings: RawQuestionSettings | null | undefined,
  field: AttemptField,
): number | undefined {
  if (!settings) {
    return undefined;
  }

  const [snakeKey, camelKey] = FIELD_KEYS[field];
  const record = settings as Record<string, unknown>;
  const raw = record[snakeKey] ?? record[camelKey];
  const value =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string' && raw.trim() !== ''
        ? Number(raw)
        : Number.NaN;

  if (!Number.isFinite(value)) {
    return undefined;
  }

  const whole = Math.trunc(value);
  return whole === 0 ? undefined : whole;
}

function limitFromStored(value: number): AttemptLimit {
  return value < 0 || value >= RETIRED_UNLIMITED_ATTEMPTS ? 'unlimited' : value;
}

export function resolveAttempts(
  chapterSettings: RawQuestionSettings | null | undefined,
  courseSettings: RawQuestionSettings | null | undefined,
  field: AttemptField,
): ResolvedAttempts {
  const chapterValue = readStoredAttempts(chapterSettings, field);

  if (chapterValue !== undefined) {
    return { limit: limitFromStored(chapterValue), source: 'chapter' };
  }

  const courseValue = readStoredAttempts(courseSettings, field);

  if (courseValue !== undefined) {
    return { limit: limitFromStored(courseValue), source: 'course' };
  }

  return { limit: DEFAULT_ATTEMPTS, source: 'default' };
}

export function resolveSectionAttempts(
  chapterSettings: RawQuestionSettings | null | undefined,
  courseSettings: RawQuestionSettings | null | undefined,
): SectionAttempts {
  return {
    exactAnswer: resolveAttempts(chapterSettings, courseSettings, 'exactAnswer'),
    multipleChoice: resolveAttempts(chapterSettings, courseSettings, 'multipleChoice'),
  };
}

// ── The settings dialogs ─────────────────────────────────────────────────────

// The select's value, which is also what a dialog saves.
export function attemptsToSelectValue(limit: AttemptLimit): number {
  return limit === 'unlimited' ? UNLIMITED_ATTEMPTS : limit;
}

// Unlimited and 1 to 10, plus a stored value outside that list, so the select
// never shows a value other than the one in effect.
export function attemptSelectOptions(
  currentValue: number,
): Array<{ label: string; value: number }> {
  const limits =
    currentValue === UNLIMITED_ATTEMPTS || LISTED_LIMITS.includes(currentValue)
      ? LISTED_LIMITS
      : [...LISTED_LIMITS, currentValue].sort((a, b) => a - b);

  return [
    { label: 'Unlimited', value: UNLIMITED_ATTEMPTS },
    ...limits.map((limit) => ({ label: String(limit), value: limit })),
  ];
}

export function attemptSourceText(
  source: AttemptSource,
  level: 'chapter' | 'course',
): string {
  if (source === 'default') {
    return 'Default: nothing saved.';
  }

  if (level === 'course') {
    return 'Set for this course.';
  }

  return source === 'chapter' ? 'Set for this chapter.' : 'From the course settings.';
}

export function attemptMeaningText(
  field: AttemptField,
  selectValue: number,
): string | null {
  if (selectValue === UNLIMITED_ATTEMPTS) {
    return 'Students can check as often as they like.';
  }

  if (selectValue === 1) {
    return 'Students see Save answer only, with no Check.';
  }

  return field === 'multipleChoice'
    ? 'A question allows at most one fewer attempt than it has options.'
    : null;
}

// ── The control panel's rows ─────────────────────────────────────────────────

function formatLimit(limit: AttemptLimit) {
  if (limit === 'unlimited') {
    return 'unlimited';
  }

  return limit === 1 ? '1 attempt' : `${limit} attempts`;
}

export function describeAttempts(attempts: SectionAttempts): string {
  const multipleChoice = attempts.multipleChoice.limit;
  const exactAnswer = attempts.exactAnswer.limit;

  if (multipleChoice === exactAnswer) {
    return multipleChoice === 'unlimited'
      ? 'Unlimited attempts.'
      : `${formatLimit(multipleChoice)} per question.`;
  }

  return `Multiple choice: ${formatLimit(multipleChoice)}; exact/numerical: ${formatLimit(exactAnswer)}.`;
}
